// @ts-check
import { useState, createContext, useEffect, useContext, JSX } from 'react';
import { useUserContext } from '../user_context/UserProvider';
import { useEmergencyUpdaterProvider } from './EmergencyUpdaterProvider';
import { SignalRContext } from '../signal_r_context/SignalRListener';
import {
  getAllEmergenciesByUser,
  getAllEmergencyByID,
} from '../../repository/EmergencyDAO';
import { invokeEmergencySRGroup } from '../../repository/SignalRDAO';
import { getGroupUserStatuses } from '../../repository/UserStatusDAO';
import { setGroupUserStatuses } from '../group_user_context/OrgGroupUserStatusStore';
import {
  EmergencyMessage,
  sortByTimestamp,
} from '../../models/Emergency/EmergencyMessage';
import { AllEmergencyObjects } from '@/models/Emergency/AllEmergencyObjects';
import { Emergency } from '@/models/Emergency/Emergency';
import { UserHistoryObservedEmergencyModel } from '@/models/Emergency/UserHistoryObservedEmergencyModel';
// Emergency store imports
import { useEmergencyActions } from '@/stores/map/emergencyStore';
import { OrgUser } from '@/models/User/OrgUser';
import { UserHistoryOnAzure } from '../../../generated-client/models';
import { UserLocation } from '@/models/User/UserLocation';
import { OrgUserModelEmergencyContactInfo } from '@/models/Emergency/OrgUserModelEmergencyContactInfo';

export const EmergencyContext = createContext<EmergencyContextType>({
  emergencies: {},
  getEmergenciesByUser: async (_: string): Promise<UserHistoryOnAzure | null> =>
    null,
  getEmergencyUserDetails: (_: OrgUser[], __: string) => null,
});

interface EmergencyContextType {
  emergencies: Record<
    string,
    {
      emergency: Emergency | null;
      messages: EmergencyMessage[];
      locations: UserLocation[];
    }
  >;
  getEmergencyUserDetails: (
    users: Array<OrgUser>,
    userAuthId: string,
  ) => OrgUserModelEmergencyContactInfo | null;
  getEmergenciesByUser: (
    userAuthId: string,
  ) => Promise<UserHistoryOnAzure | null>;
}

export const EmergencyProvider = ({ children }: { children: JSX.Element }) => {
  const { activeEmergencyMessages, observedEmergencies } =
    useEmergencyUpdaterProvider();
  const connection = useContext(SignalRContext);
  const { bearerToken } = useUserContext();
  const { setShowEmergencyListModal } = useEmergencyActions();
  /**
   *
   * @description This state holds the emergencies observed by the user, where the key is the emergency ID and the value is an object containing the emergency and its associated messages.
   */
  const [emergencies, setEmergencies] = useState<
    Record<
      string,
      {
        emergency: Emergency | null;
        messages: EmergencyMessage[];
        locations: UserLocation[];
      }
    >
  >({});

  const loadGroupUserStatuses = async (groupName: string) => {
    try {
      const groupUserStatuses = await getGroupUserStatuses(groupName);
      setGroupUserStatuses(groupName, groupUserStatuses);
    } catch (error) {
      console.error('Failed to load group user statuses for', groupName, error);
    }
  };

  useEffect(() => {
    if (observedEmergencies && Object.keys(observedEmergencies).length > 0) {
      const fetchData = async () => {
        let emergenciesCopy = { ...emergencies };
        let hasChanges = false;
        for (const [key, rawEmergency] of Object.entries(observedEmergencies)) {
          const observedEmergency = new UserHistoryObservedEmergencyModel(
            rawEmergency,
          );
          const existingEmergency = emergenciesCopy[key];
          if (!existingEmergency && observedEmergency.isActiveEmergency()) {
            await invokeEmergency(key);
            try {
              const rawEmergencyObjects = await fetchOrgEmergencyData(
                key,
                observedEmergency.org_code,
              );
              const emergencyObjects = new AllEmergencyObjects({
                emergency: rawEmergencyObjects.emergency,
                emergency_messages: rawEmergencyObjects.emergency_messages,
                locations: rawEmergencyObjects.locations,
              });
              if (!emergenciesCopy[key])
                emergenciesCopy[key] = {
                  emergency: null,
                  messages: [],
                  locations: [],
                };
              if (emergencyObjects.emergency)
                emergenciesCopy[key].emergency =
                  emergencyObjects.emergency instanceof Emergency
                    ? emergencyObjects.emergency
                    : new Emergency(emergencyObjects.emergency);

              if (
                emergencyObjects.emergency_messages &&
                Array.isArray(emergencyObjects.emergency_messages)
              ) {
                emergencyObjects.emergency_messages.forEach((message) => {
                  emergenciesCopy[key].messages.push(
                    message instanceof EmergencyMessage
                      ? message
                      : new EmergencyMessage(message),
                  );
                });
                emergenciesCopy[key].messages = sortByTimestamp(
                  emergenciesCopy[key].messages,
                );
              }
              if (emergencyObjects.locations) {
                emergenciesCopy[key].locations = emergencyObjects.locations;
              }
              // Force a re-load of the group user statuses for the emergency's group to get the latest user locations
              await loadGroupUserStatuses(observedEmergency.group_name);
              // mark to show the list of emergencies
              setShowEmergencyListModal(true);
              hasChanges = true;
            } catch (e) {
              console.error(e);
            }
          } else if (
            existingEmergency?.emergency &&
            !observedEmergency.isActiveEmergency()
          ) {
            existingEmergency.emergency.resolved_timestamp =
              observedEmergency.resolved_timestamp;
            hasChanges = true;
          }
        }
        if (hasChanges) {
          setEmergencies(emergenciesCopy);
        }
      };
      fetchData();
    }
  }, [observedEmergencies]);

  useEffect(() => {
    setEmergencies((prevEmergencies) => {
      const updatedEmergencies = { ...prevEmergencies };
      Object.entries(activeEmergencyMessages).forEach(
        ([emergencyId, messages]) => {
          if (updatedEmergencies[emergencyId]) {
            messages.forEach((newMessage) => {
              const existingMessages = updatedEmergencies[emergencyId].messages;
              const messageIndex = existingMessages.findIndex(
                (message) => message.id === newMessage.id,
              );
              if (messageIndex >= 0)
                existingMessages[messageIndex] =
                  newMessage instanceof EmergencyMessage
                    ? newMessage
                    : new EmergencyMessage(newMessage);
              else
                existingMessages.push(
                  newMessage instanceof EmergencyMessage
                    ? newMessage
                    : new EmergencyMessage(newMessage),
                );
              updatedEmergencies[emergencyId].messages =
                sortByTimestamp(existingMessages);
            });
          }
        },
      );
      return updatedEmergencies;
    });
  }, [activeEmergencyMessages]);

  const invokeEmergency = async (emergencyId: string) => {
    if (
      connection &&
      connection.connection &&
      connection.connection.connectionId
    ) {
      try {
        await invokeEmergencySRGroup(
          true,
          connection.connection.connectionId,
          emergencyId,
          bearerToken,
        );
      } catch (e) {
        console.log(e);
      }
    }
  };

  const fetchOrgEmergencyData = async (
    emergencyId: string,
    orgCode: string,
  ) => {
    return await getAllEmergencyByID(emergencyId, orgCode);
  };

  const getEmergencyUserDetails = (users: OrgUser[], userAuthId: string) => {
    return users.find((u) => u.auth_id === userAuthId) || null;
  };

  /**
   *
   * @param {string} userAuthId
   * @returns {Promise<import('../../../generated-client/models').UserHistoryOnAzure>} List of emergencies associated with the user
   * @throws {Error} If the fetch operation fails
   */
  const getEmergenciesByUser = async (
    userAuthId: string,
  ): Promise<UserHistoryOnAzure> => {
    try {
      return await getAllEmergenciesByUser(userAuthId);
    } catch (e) {
      throw new Error('Failed to fetch emergencies for user');
    }
  };

  return (
    <EmergencyContext.Provider
      value={{ emergencies, getEmergencyUserDetails, getEmergenciesByUser }}
    >
      {children}
    </EmergencyContext.Provider>
  );
};

/**
 *
 * @returns {EmergencyContextType}
 */
export const useEmergencyProvider = () => {
  return useContext(EmergencyContext);
};
